// Mock health check data for different scenarios
// Asset IDs are zone-scoped numeric IDs (1, 2, 3 per zone)
// The health check is triggered from the asset detail page with the asset's zone-scoped ID
//
// Finishing zone: 1=CNC Lathe (Critical), 2=Hydraulic Pump (Medium), 3=Main Drive Motor (Low)
// Assembly zone:  1=Welding Robot (Critical), 2=Torque Station (Medium), 3=Assembly Conveyor (Low)
// Packaging zone: 1=Heat Sealer (Critical), 2=Label Printer (Medium), 3=Palletizer (Medium)

export const healthCheckScenarios = {
  // ─── FINISHING ZONE ───

  'finishing-zone-1': {
    // CNC Lathe (Critical) - Bearing failure, parts NOT available
    scenario: 'failure',
    partsAvailable: true,
    diagnosis: {
      title: 'Bearing failure',
      description: 'Recommended to proceed with immediate replacement as planned',
      aiAnalysis: 'See AI Analysis',
      warning: 'The technician should ensure that a physical inspection is conducted to support the AI analysis.',
      details: 'Identified abnormal vibration, looks like a bearing issue'
    },
    aiAnalysisDetails: {
      confidence: 94,
      model: 'PdM-VibroAnalyzer v3.2',
      analysisTimestamp: 'Feb 19, 2026, 10:33 AM',
      summary: 'High-frequency vibration signature matches bearing inner-race defect pattern with 94% confidence. Thermal anomaly in the bearing housing confirms mechanical friction increase.',
      findings: [
        { label: 'Root Cause', value: 'Inner-race bearing defect (BPFI frequency spike at 162 Hz)' },
        { label: 'Severity', value: 'Critical — Immediate replacement recommended' },
        { label: 'Anomaly First Detected', value: 'Feb 17, 2026, 03:15 AM' }
      ],
      contributingFactors: [
        'Vibration amplitude increased 116% over the last 72 hours',
        'Bearing temperature 58% above normal operating range',
        'Lubricant degradation suspected based on harmonic pattern shift'
      ],
      aiNotes: [
        { type: 'warning', text: 'Do not operate this asset beyond low-speed idle until the bearing is replaced. Running at full RPM risks seizure and shaft damage.' },
        { type: 'action', text: 'Inspect the bearing housing for discoloration or heat marks — this will confirm whether thermal damage has already occurred and if the housing also needs replacement.' },
        { type: 'action', text: 'Check lubricant condition in adjacent bearings. Degradation in one bearing often indicates contamination that may affect nearby components.' },
        { type: 'info', text: 'Historical data shows this bearing type (6205-2RS) has an average lifespan of 18 months under current load conditions. Consider upgrading to a ceramic hybrid bearing for extended service life.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 6.5, unit: 'mm/s', normal: '0-3', status: 'critical', reportedTimestamp: 'Feb 19, 2026, 10:32 AM' },
      { name: 'Temperature', value: 95, unit: '°C', normal: '20-60', status: 'critical', reportedTimestamp: 'Feb 19, 2026, 10:32 AM' },
      { name: 'Pressure', value: 2.8, unit: 'bar', normal: '2.5-3.2', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:32 AM' }
    ],
    requiredParts: [
      { name: 'Deep Grove Ball Bearing', partNumber: 'BRG-6205-2RS', status: 'available', quantity: 4, shelf: 'Shelf 12' }
    ],
    requestId: 'RID-2024-134'
  },

  'finishing-zone-2': {
    // Hydraulic Pump (Medium) - Seal degradation, parts available
    scenario: 'failure',
    partsAvailable: true,
    diagnosis: {
      title: 'Hydraulic seal degradation',
      description: 'AI detected pressure decline pattern consistent with seal wear',
      aiAnalysis: 'See AI Analysis',
      warning: 'Physical inspection recommended to verify AI assessment.',
      details: 'Pressure below normal range, temperature elevated — likely seal and oil contamination'
    },
    aiAnalysisDetails: {
      confidence: 85,
      model: 'PdM-HydroSense v1.5',
      analysisTimestamp: 'Feb 19, 2026, 10:29 AM',
      summary: 'Pressure fluctuation pattern is consistent with a slow hydraulic seal leak. Temperature elevation suggests oil contamination or degradation.',
      findings: [
        { label: 'Root Cause', value: 'Hydraulic seal degradation causing slow fluid leak' },
        { label: 'Severity', value: 'Medium — Schedule replacement within 2 weeks' },
        { label: 'Failure Prediction', value: '~14 days before pressure falls below operational threshold' },
        { label: 'Anomaly First Detected', value: 'Feb 16, 2026, 11:20 AM' }
      ],
      contributingFactors: [
        'Pressure dropped to 2.3 bar (below 2.5 bar normal minimum)',
        'Temperature elevated to 68°C (above 60°C normal max)',
        'Vibration at 4.2 mm/s indicates compensatory hydraulic adjustments'
      ],
      aiNotes: [
        { type: 'warning', text: 'Monitor hydraulic fluid level daily until the seal is replaced. If the level drops below the minimum mark, shut down the asset to avoid pump cavitation.' },
        { type: 'action', text: 'Visually inspect the hydraulic cylinder rod and fittings for oil residue or wet spots to pinpoint the exact leak location before ordering parts.' },
        { type: 'action', text: 'Check the hydraulic fluid for contamination (milky appearance indicates water ingress). Contaminated fluid must be replaced along with the seals.' },
        { type: 'info', text: 'This is a slow leak and the asset can continue operating at reduced duty for up to 14 days. Schedule the repair during the next planned downtime window if possible.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 4.2, unit: 'mm/s', normal: '0-3', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:28 AM' },
      { name: 'Temperature', value: 68, unit: '°C', normal: '20-60', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:28 AM' },
      { name: 'Pressure', value: 2.3, unit: 'bar', normal: '2.5-3.2', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:28 AM' }
    ],
    requiredParts: [
      { name: 'Hydraulic Seal Kit', partNumber: 'HSK-4420-A', status: 'available', quantity: 2, shelf: 'Shelf 8' },
      { name: 'Oil Filter', partNumber: 'OF-3301', status: 'available', quantity: 1, shelf: 'Shelf 8' }
    ],
    requestId: 'RID-2024-135'
  },

  'finishing-zone-3': {
    // Main Drive Motor (Low risk) - All systems healthy
    scenario: 'healthy',
    partsAvailable: true,
    diagnosis: {
      title: 'Systems Healthy',
      description: 'All systems are operating within acceptable parameters. Temperature at threshold but stable.',
      aiAnalysis: 'See AI Analysis'
    },
    aiAnalysisDetails: {
      confidence: 85,
      model: 'PdM-HealthMonitor v3.0',
      analysisTimestamp: 'Feb 19, 2026, 10:26 AM',
      summary: 'Temperature at critical threshold (80°C) but stable. Vibration slightly elevated but not trending upward. No immediate action required but continued monitoring recommended.',
      findings: [
        { label: 'Overall Status', value: 'Monitoring — Temperature at threshold' },
        { label: 'Vibration Health', value: 'Slightly elevated (4.2 mm/s — above 3 mm/s normal max)' },
        { label: 'Thermal Health', value: 'At threshold (80°C — limit is 80°C)' },
        { label: 'Next Scheduled Maintenance', value: 'Apr 20, 2026' }
      ],
      contributingFactors: [
        'Temperature stable at 80°C over last 24 hours (not trending up)',
        'Vibration elevated but stable — no acceleration trend',
        'Pressure within normal range at 2.9 bar'
      ],
      aiNotes: [
        { type: 'info', text: 'Temperature is at the threshold but stable. If readings begin to increase, escalate to maintenance immediately.' },
        { type: 'action', text: 'Continue routine monitoring schedule. Next inspection in 30 days per current plan.' },
        { type: 'info', text: 'Vibration at 4.2 mm/s is above normal but consistent with motor age. Consider scheduling winding test during next planned downtime.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 4.2, unit: 'mm/s', normal: '0-3', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:25 AM' },
      { name: 'Temperature', value: 80, unit: '°C', normal: '20-60', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:25 AM' },
      { name: 'Pressure', value: 2.9, unit: 'bar', normal: '2.5-3.2', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:25 AM' }
    ],
    requiredParts: []
  },

  // ─── ASSEMBLY ZONE ───

  'assembly-zone-1': {
    // Welding Robot (Critical) - Calibration failure
    scenario: 'failure',
    partsAvailable: true,
    diagnosis: {
      title: 'Calibration failure detected',
      description: 'Critical calibration drift causing assembly defects',
      aiAnalysis: 'See AI Analysis',
      warning: 'Asset must be recalibrated immediately to prevent further assembly defects.',
      details: 'Calibration drifted 0.8mm beyond tolerance, assembly defect rate increased 12%'
    },
    aiAnalysisDetails: {
      confidence: 92,
      model: 'PdM-RoboAlign v2.1',
      analysisTimestamp: 'Feb 19, 2026, 10:31 AM',
      summary: 'Calibration drift of 0.8mm detected on welding robot. Vibration signature in joint 3 confirms mechanical misalignment. Assembly defect rate increased 12% correlating with drift timeline.',
      findings: [
        { label: 'Root Cause', value: 'Mechanical misalignment in joint 3 causing calibration drift' },
        { label: 'Severity', value: 'Critical — Immediate recalibration required' },
        { label: 'Failure Prediction', value: '~3 days before defect rate exceeds 20%' },
        { label: 'Anomaly First Detected', value: 'Feb 17, 2026, 09:00 AM' }
      ],
      contributingFactors: [
        'Calibration drifted 0.8mm beyond 0.3mm tolerance',
        'Vibration in joint 3 at 5.8 mm/s (well above 3 mm/s normal)',
        'Assembly defect rate increased from 2% to 14% this week'
      ],
      aiNotes: [
        { type: 'warning', text: 'Do not continue welding operations until recalibration is complete. Continued use risks further defective assemblies and potential safety issues.' },
        { type: 'action', text: 'Use laser alignment tool to measure exact drift in all 6 axes. The AI detected joint 3 as the primary contributor — verify this measurement first.' },
        { type: 'action', text: 'Inspect the servo motor in joint 3 for backlash or encoder drift. Calibration drift of this magnitude often indicates servo motor wear.' },
        { type: 'info', text: 'Previous calibration was performed 75 days ago. Normal calibration interval for this model is 60 days. Consider shortening the calibration schedule.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 5.8, unit: 'mm/s', normal: '0-3', status: 'critical', reportedTimestamp: 'Feb 19, 2026, 10:30 AM' },
      { name: 'Temperature', value: 72, unit: '°C', normal: '20-65', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:30 AM' },
      { name: 'Pressure', value: 3.1, unit: 'bar', normal: '2.5-3.2', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:30 AM' }
    ],
    requiredParts: [
      { name: 'Calibration Module', partNumber: 'CM-2200-X', status: 'available', quantity: 1, shelf: 'Shelf 6' },
      { name: 'Servo Motor', partNumber: 'SM-J3-400', status: 'available', quantity: 1, shelf: 'Shelf 6' }
    ],
    requestId: 'RID-2024-140'
  },

  'assembly-zone-2': {
    // Torque Station (Medium) - Torque sensor degradation, parts available
    scenario: 'failure',
    partsAvailable: true,
    diagnosis: {
      title: 'Torque sensor degradation',
      description: 'AI detected inconsistent torque values indicating sensor wear',
      aiAnalysis: 'See AI Analysis',
      warning: 'Physical verification of torque output recommended before sensor replacement.',
      details: '15% variance in torque output over last 500 cycles'
    },
    aiAnalysisDetails: {
      confidence: 88,
      model: 'PdM-TorqueGuard v1.8',
      analysisTimestamp: 'Feb 19, 2026, 10:28 AM',
      summary: 'Torque output shows 15% variance over 500 cycles. Pattern is consistent with sensor degradation rather than mechanical failure — physical readings (temp, vibration, pressure) are all within normal range.',
      findings: [
        { label: 'Root Cause', value: 'Torque sensor signal degradation causing measurement variance' },
        { label: 'Severity', value: 'Medium — Schedule replacement within 1 week' },
        { label: 'Failure Prediction', value: '~7 days before variance exceeds 25% tolerance' },
        { label: 'Anomaly First Detected', value: 'Feb 15, 2026, 10:00 AM' }
      ],
      contributingFactors: [
        '15% variance detected across 500 cycles (normal: <5%)',
        'All physical sensor readings within normal range',
        'Sensor age exceeds recommended replacement interval by 2 months'
      ],
      aiNotes: [
        { type: 'warning', text: 'Verify torque output with a calibrated external torque wrench before concluding sensor failure. If external reading matches sensor output, the issue may be mechanical.' },
        { type: 'action', text: 'Replace the torque sensor and control board together — the variance pattern suggests both components may be degraded.' },
        { type: 'info', text: 'Asset can continue operating at reduced throughput. Monitor quality output closely until replacement is completed.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 2.1, unit: 'mm/s', normal: '0-3', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:27 AM' },
      { name: 'Temperature', value: 52, unit: '°C', normal: '20-60', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:27 AM' },
      { name: 'Pressure', value: 2.7, unit: 'bar', normal: '2.5-3.2', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:27 AM' }
    ],
    requiredParts: [
      { name: 'Torque Sensor', partNumber: 'TS-500-SEN', status: 'available', quantity: 1, shelf: 'Shelf 10' },
      { name: 'Control Board', partNumber: 'CB-TS500', status: 'available', quantity: 1, shelf: 'Shelf 10' }
    ],
    requestId: 'RID-2024-141'
  },

  'assembly-zone-3': {
    // Assembly Conveyor (Low risk) - All systems healthy
    scenario: 'healthy',
    partsAvailable: true,
    diagnosis: {
      title: 'Systems Healthy',
      description: 'All systems are operating within normal parameters. Belt tension declining but within operational range.',
      aiAnalysis: 'See AI Analysis'
    },
    aiAnalysisDetails: {
      confidence: 90,
      model: 'PdM-HealthMonitor v3.0',
      analysisTimestamp: 'Feb 19, 2026, 10:21 AM',
      summary: 'All sensor readings are within normal operating parameters. Belt tension is gradually declining but still within operational range. Replacement recommended within 60 days.',
      findings: [
        { label: 'Overall Status', value: 'Healthy — Preventive maintenance planned' },
        { label: 'Vibration Health', value: 'Normal (1.8 mm/s — within 0-3 mm/s range)' },
        { label: 'Thermal Health', value: 'Normal (45°C — within 20-60°C range)' },
        { label: 'Next Scheduled Maintenance', value: 'Mar 1, 2026 (Belt tension adjustment)' }
      ],
      contributingFactors: [
        'All readings within normal range',
        'Belt tension declining gradually but still operational',
        'Replacement belt already ordered'
      ],
      aiNotes: [
        { type: 'info', text: 'All parameters are within normal range. Belt tension decline is expected and being tracked.' },
        { type: 'action', text: 'Belt tension adjustment scheduled for Mar 1, 2026. Ensure replacement belt is available by Feb 25.' },
        { type: 'info', text: 'No corrective action needed at this time. Continue standard monitoring schedule.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 1.8, unit: 'mm/s', normal: '0-3', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:20 AM' },
      { name: 'Temperature', value: 45, unit: '°C', normal: '20-60', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:20 AM' },
      { name: 'Pressure', value: 2.9, unit: 'bar', normal: '2.5-3.2', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:20 AM' }
    ],
    requiredParts: []
  },

  // ─── PACKAGING ZONE ───

  'packaging-zone-1': {
    // Heat Sealer (Critical) - Overheating, parts NOT available
    scenario: 'failure',
    partsAvailable: false,
    diagnosis: {
      title: 'Heating element overheating',
      description: 'Critical temperature exceedance detected — potential fire hazard',
      aiAnalysis: 'See AI Analysis',
      warning: 'Asset must be shut down immediately. Fire safety protocol initiated.',
      details: 'Temperature 15°C above normal operating range. Heating element thermostat malfunction suspected.'
    },
    aiAnalysisDetails: {
      confidence: 95,
      model: 'PdM-ThermalGuard v2.8',
      analysisTimestamp: 'Feb 19, 2026, 10:36 AM',
      summary: 'Heat sealer running 15°C above normal operating temperature (105°C vs 90°C max). Heating element thermostat malfunction confirmed. Risk of seal quality failure and potential fire hazard.',
      findings: [
        { label: 'Root Cause', value: 'Heating element thermostat malfunction causing uncontrolled temperature rise' },
        { label: 'Severity', value: 'Critical — Immediate shutdown required' },
        { label: 'Failure Prediction', value: '~6 hours before fire safety threshold is reached' },
        { label: 'Anomaly First Detected', value: 'Feb 19, 2026, 08:30 AM' }
      ],
      contributingFactors: [
        'Temperature at 105°C, 15°C above 90°C normal max',
        'Vibration slightly elevated at 3.5 mm/s due to thermal expansion',
        'Pressure elevated at 3.4 bar due to thermal effects on sealing mechanism'
      ],
      aiNotes: [
        { type: 'warning', text: 'Shut down the heat sealer immediately and allow it to cool before any inspection. Do not attempt to operate even at reduced temperature until the thermostat is replaced.' },
        { type: 'action', text: 'Inspect the heating element and thermostat for visible damage. Check wiring harness for heat damage or discoloration.' },
        { type: 'action', text: 'Test the thermostat with a multimeter to confirm malfunction. If thermostat reads open circuit, replacement is confirmed.' },
        { type: 'info', text: 'Heating elements in this model have an average lifespan of 2 years. This unit is at 22 months. Consider replacing the element along with the thermostat as preventive measure.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 3.5, unit: 'mm/s', normal: '0-3', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:35 AM' },
      { name: 'Temperature', value: 105, unit: '°C', normal: '60-90', status: 'critical', reportedTimestamp: 'Feb 19, 2026, 10:35 AM' },
      { name: 'Pressure', value: 3.4, unit: 'bar', normal: '2.5-3.2', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:35 AM' }
    ],
    requiredParts: [
      { name: 'Heating Element', partNumber: 'HE-800-A', status: 'out_of_stock', quantity: 1, shelf: 'Shelf 4' },
      { name: 'Thermostat', partNumber: 'TH-800-B', status: 'out_of_stock', quantity: 1, shelf: 'Shelf 4' }
    ],
    requestId: 'RID-2024-150'
  },

  'packaging-zone-2': {
    // Label Printer (Medium) - Print head misalignment, parts available
    scenario: 'failure',
    partsAvailable: true,
    diagnosis: {
      title: 'Print head misalignment',
      description: 'Label placement accuracy below threshold — print head realignment needed',
      aiAnalysis: 'See AI Analysis',
      warning: 'Physical verification of label placement recommended before adjustment.',
      details: '8% of labels placed outside tolerance in last batch'
    },
    aiAnalysisDetails: {
      confidence: 82,
      model: 'PdM-PrintAlign v1.2',
      analysisTimestamp: 'Feb 19, 2026, 10:23 AM',
      summary: 'Print head alignment has drifted 0.5mm from baseline. 8% of labels in last batch placed outside tolerance. All environmental sensors within normal range — issue is mechanical alignment.',
      findings: [
        { label: 'Root Cause', value: 'Print head mechanical drift causing label misalignment' },
        { label: 'Severity', value: 'Medium — Realign within 2 days' },
        { label: 'Failure Prediction', value: '~2 days before misalignment exceeds 15% rejection rate' },
        { label: 'Anomaly First Detected', value: 'Feb 18, 2026, 11:00 AM' }
      ],
      contributingFactors: [
        '8% label rejection rate (threshold: 5%)',
        'Print head position drifted 0.5mm from baseline',
        'All environmental sensors within normal range'
      ],
      aiNotes: [
        { type: 'warning', text: 'Review the last batch output and quarantine any mislabeled products before they ship. 8% misalignment may have affected downstream processes.' },
        { type: 'action', text: 'Realign the print head using the manufacturer alignment tool. Check the print head rail for debris or wear that may have caused the drift.' },
        { type: 'info', text: 'Previous alignment was 30 days ago. Normal alignment interval for this model is 45 days, but high-throughput batches may require more frequent checks.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 2.5, unit: 'mm/s', normal: '0-3', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:22 AM' },
      { name: 'Temperature', value: 55, unit: '°C', normal: '20-60', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:22 AM' },
      { name: 'Pressure', value: 2.6, unit: 'bar', normal: '2.5-3.2', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:22 AM' }
    ],
    requiredParts: [
      { name: 'Print Head Assembly', partNumber: 'PH-400X', status: 'available', quantity: 1, shelf: 'Shelf 9' }
    ],
    requestId: 'RID-2024-151'
  },

  'packaging-zone-3': {
    // Palletizer (Medium) - Hydraulic pressure decline, parts available
    scenario: 'failure',
    partsAvailable: true,
    diagnosis: {
      title: 'Hydraulic pressure decline',
      description: 'AI detected gradual hydraulic pressure decline indicating seal wear',
      aiAnalysis: 'See AI Analysis',
      warning: 'Physical inspection recommended to verify AI assessment.',
      details: 'Pressure at 2.2 bar (below 2.5 bar minimum), vibration elevated due to hydraulic compensation'
    },
    aiAnalysisDetails: {
      confidence: 78,
      model: 'PdM-HydroSense v1.5',
      analysisTimestamp: 'Feb 19, 2026, 10:19 AM',
      summary: 'Hydraulic pressure showing gradual decline over past 2 weeks. Pressure now at 2.2 bar, below the 2.5 bar operational minimum. Pattern consistent with seal degradation.',
      findings: [
        { label: 'Root Cause', value: 'Hydraulic seal degradation causing pressure decline' },
        { label: 'Severity', value: 'Medium — Schedule seal replacement within 1 week' },
        { label: 'Failure Prediction', value: '~7 days before pressure falls below safe operating level' },
        { label: 'Anomaly First Detected', value: 'Feb 12, 2026, 03:00 PM' }
      ],
      contributingFactors: [
        'Pressure at 2.2 bar (below 2.5 bar minimum)',
        'Vibration elevated to 3.8 mm/s due to hydraulic compensation',
        'Temperature near threshold at 58°C'
      ],
      aiNotes: [
        { type: 'warning', text: 'Monitor hydraulic fluid level daily. If level drops below minimum mark, shut down to prevent pump cavitation damage.' },
        { type: 'action', text: 'Inspect hydraulic cylinder rod and fittings for oil residue to confirm leak location.' },
        { type: 'action', text: 'Replace hydraulic seal kit and fluid during repair. Fluid may be contaminated from seal debris.' },
        { type: 'info', text: 'Asset can operate at reduced load for approximately 7 more days. Schedule repair during next planned downtime.' }
      ]
    },
    sensorReadings: [
      { name: 'Vibration', value: 3.8, unit: 'mm/s', normal: '0-3', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:18 AM' },
      { name: 'Temperature', value: 58, unit: '°C', normal: '20-60', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:18 AM' },
      { name: 'Pressure', value: 2.2, unit: 'bar', normal: '2.5-3.2', status: 'warning', reportedTimestamp: 'Feb 19, 2026, 10:18 AM' }
    ],
    requiredParts: [
      { name: 'Hydraulic Seal Kit', partNumber: 'HSK-1200-B', status: 'available', quantity: 1, shelf: 'Shelf 7' },
      { name: 'Hydraulic Fluid', partNumber: 'HF-46', status: 'available', quantity: 2, shelf: 'Shelf 7' }
    ],
    requestId: 'RID-2024-152'
  },
};

// Default fallback returns healthy scenario
const defaultHealthy = {
  scenario: 'healthy',
  partsAvailable: true,
  diagnosis: {
    title: 'Systems Healthy',
    description: 'All systems are operating perfectly',
    aiAnalysis: 'See AI Analysis'
  },
  aiAnalysisDetails: {
    confidence: 99,
    model: 'PdM-HealthMonitor v3.0',
    analysisTimestamp: 'Feb 19, 2026, 10:20 AM',
    summary: 'All sensor readings are within normal operating parameters. No anomalies detected. Asset is performing optimally.',
    findings: [
      { label: 'Overall Status', value: 'Healthy — No action required' },
      { label: 'Vibration Health', value: 'Normal — within expected range' },
      { label: 'Thermal Health', value: 'Normal — within expected range' },
      { label: 'Next Scheduled Maintenance', value: 'On schedule' }
    ],
    contributingFactors: [
      'All readings stable over the monitoring period',
      'No deviation trends detected',
      'Operating within manufacturer specifications'
    ],
    aiNotes: [
      { type: 'info', text: 'All parameters are within normal range. No corrective action is needed.' },
      { type: 'action', text: 'Follow the standard preventive maintenance schedule. No additional inspections required.' },
      { type: 'info', text: 'Asset is operating within manufacturer specifications. Continue monitoring as usual.' }
    ]
  },
  sensorReadings: [
    { name: 'Vibration', value: 2.1, unit: 'mm/s', normal: '0-3', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:20 AM' },
    { name: 'Temperature', value: 55, unit: '°C', normal: '20-60', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:20 AM' },
    { name: 'Pressure', value: 2.7, unit: 'bar', normal: '2.5-3.2', status: 'normal', reportedTimestamp: 'Feb 19, 2026, 10:20 AM' }
  ],
  requiredParts: []
};

export const getHealthCheckData = (assetId, zoneId) => {
  // Try zone-scoped key first
  if (zoneId) {
    const zoneKey = `${zoneId}-${assetId}`;
    if (healthCheckScenarios[zoneKey]) return healthCheckScenarios[zoneKey];
  }
  // Fallback to default healthy
  return defaultHealthy;
};
